import { useEffect, useMemo, useState } from "react";
import {
  Anchor,
  Badge,
  Card,
  Container,
  Group,
  Loader,
  Progress,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconBuildingFactory2,
  IconAntenna,
  IconDeviceMobile,
  IconTruck,
  IconUsers,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import type { DeliveryTypeBreakdownRow, PeriodStats, StatsOverview } from "../api/types";
import { deliveryTypeLabel } from "../lib/shipmentLabels";
import { useCurrentUser } from "../hooks/useCurrentUser";

export function Dashboard() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const [stats, setStats] = useState<StatsOverview | null>(null);
  const [health, setHealth] = useState<{ status: string; db: boolean } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void api.health().then(setHealth).catch(() => undefined);
    void api
      .stats()
      .then((s) => setStats(s))
      .finally(() => setLoading(false));
  }, []);

  const totalDevices = stats?.counts.devices ?? 0;
  const rmaCount =
    (stats?.device_by_status.returned ?? 0) + (stats?.device_by_status.defect ?? 0);

  return (
    <Container size="xl">
      <Group justify="space-between" mb="md" wrap="wrap">
        <div>
          <Title order={2}>{t("nav.dashboard")}</Title>
          <Text c="dimmed">{t("dashboard.welcome", { name: user.username })}</Text>
        </div>
        <Group gap="xs">
          <Badge color={health?.status === "ok" ? "green" : "red"} variant="light">
            {t("dashboard.apiStatus", { status: health?.status ?? "?" })}
          </Badge>
          <Badge color={health?.db ? "green" : "red"} variant="light">
            {t("dashboard.dbStatus", { status: health?.db ? t("dashboard.dbOk") : t("dashboard.dbDown") })}
          </Badge>
        </Group>
      </Group>

      {loading || !stats ? (
        <Loader />
      ) : (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} spacing="md">
            <StatCard
              icon={<IconUsers size={20} />}
              label={t("dashboard.statCustomers")}
              value={stats.counts.customers}
              to="/customers"
              color="blue"
            />
            <StatCard
              icon={<IconBuildingFactory2 size={20} />}
              label={t("dashboard.statBatches")}
              value={stats.counts.batches}
              to="/batches"
              color="indigo"
            />
            <StatCard
              icon={<IconTruck size={20} />}
              label={t("dashboard.statShipments")}
              value={stats.counts.shipments}
              to="/shipments"
              color="teal"
            />
            <StatCard
              icon={<IconDeviceMobile size={20} />}
              label={t("dashboard.statDevices")}
              value={stats.counts.devices}
              color="cyan"
              extra={
                rmaCount > 0 ? (
                  <Anchor component={Link} to="/rma" size="xs" c="red">
                    {rmaCount} RMA
                  </Anchor>
                ) : null
              }
            />
            <StatCard
              icon={<IconAntenna size={20} />}
              label={t("dashboard.statAntennas")}
              value={stats.counts.antennas ?? 0}
              color="grape"
            />
          </SimpleGrid>

          {stats.device_by_status?.defect != null && stats.counts.devices > 0 && (
            <Card withBorder>
              <Text size="xs" c="dimmed">{t("dashboard.defectRate")}</Text>
              <Group gap="xs" align="baseline">
                <Text size="xl" fw={700} c="red">
                  {stats.device_by_status.defect}
                </Text>
                <Text size="sm" c="dimmed">
                  / {stats.counts.devices} ({((stats.device_by_status.defect / stats.counts.devices) * 100).toFixed(1)}%)
                </Text>
              </Group>
            </Card>
          )}

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Card withBorder>
              <Title order={5} mb="sm">
                {t("dashboard.devicesByStatus")}
              </Title>
              <BreakdownList
                data={stats.device_by_status}
                total={totalDevices}
                colorMap={DEVICE_STATUS_COLORS}
              />
            </Card>

            <Card withBorder>
              <Title order={5} mb="sm">
                {t("dashboard.shipmentsByStatus")}
              </Title>
              <BreakdownList
                data={stats.shipment_by_status}
                total={stats.counts.shipments}
                colorMap={SHIPMENT_STATUS_COLORS}
              />
            </Card>

            <Card withBorder>
              <Title order={5} mb="sm">
                {t("dashboard.shipmentsByDeliveryType")}
              </Title>
              <DeliveryTypeBreakdown
                rows={stats.shipment_by_delivery_type ?? []}
                total={stats.counts.shipments}
              />
            </Card>
          </SimpleGrid>

          <PeriodStatsCard />

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Card withBorder>
              <Title order={5} mb="sm">
                {t("dashboard.activity7d")}
              </Title>
              <ActivityChart byDay={stats.activity_by_day} />
            </Card>

            <Card withBorder>
              <Group justify="space-between" mb="sm">
                <Title order={5}>{t("dashboard.recentActivity")}</Title>
                <Anchor component={Link} to="/audit" size="xs">
                  {t("dashboard.viewAll")}
                </Anchor>
              </Group>
              <ScrollArea h={280} type="auto">
                <Stack gap={6}>
                  {stats.recent_events.length === 0 && (
                    <Text c="dimmed" size="sm">
                      {t("dashboard.noEvents")}
                    </Text>
                  )}
                  {stats.recent_events.map((ev) => (
                    <Group key={ev.id} gap="xs" wrap="nowrap" align="flex-start">
                      <Text
                        size="xs"
                        c="dimmed"
                        ff="monospace"
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {new Date(ev.created_at).toLocaleString(undefined, {
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Text>
                      <Badge size="xs" variant="light" color={actionColor(ev.action)}>
                        {ev.action}
                      </Badge>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {ev.entity_type
                          ? `${ev.entity_type}${ev.entity_id ? ` #${ev.entity_id}` : ""}`
                          : ""}
                        {ev.username ? ` · ${ev.username}` : ""}
                      </Text>
                    </Group>
                  ))}
                </Stack>
              </ScrollArea>
            </Card>
          </SimpleGrid>
        </Stack>
      )}
    </Container>
  );
}

function StatCard({
  icon,
  label,
  value,
  color,
  to,
  extra,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
  to?: string;
  extra?: React.ReactNode;
}) {
  const inner = (
    <Card withBorder className={to ? "app-lift" : undefined} style={{ height: "100%" }}>
      <Group gap="xs" mb={4}>
        <Badge size="sm" variant="light" color={color}>
          {icon}
        </Badge>
        <Text size="sm" c="dimmed">
          {label}
        </Text>
      </Group>
      <Title order={2}>{value}</Title>
      {extra && <div style={{ marginTop: 4 }}>{extra}</div>}
    </Card>
  );
  return to ? (
    <Anchor component={Link} to={to} underline="never" c="inherit">
      {inner}
    </Anchor>
  ) : (
    inner
  );
}

const DELIVERY_TYPE_COLORS: Record<string, string> = {
  normal: "blue",
  debt: "red",
  defect_exchange: "orange",
};

function DeliveryTypeBreakdown({
  rows,
  total,
}: {
  rows: DeliveryTypeBreakdownRow[];
  total: number;
}) {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        {t("dashboard.noShipments")}
      </Text>
    );
  }
  return (
    <Stack gap={6}>
      {rows.map((r) => {
        const label = deliveryTypeLabel(t, r.slug, r.name) || t("dashboard.noDeliveryType");
        const pct = total > 0 ? (r.shipments / total) * 100 : 0;
        const color = DELIVERY_TYPE_COLORS[r.slug ?? ""] ?? "gray";
        return (
          <div key={`${r.slug ?? ""}-${r.name ?? ""}`}>
            <Group justify="space-between" mb={2}>
              <Badge size="xs" variant="light" color={color}>
                {label}
              </Badge>
              <Text size="xs" ff="monospace">
                {r.shipments} ({pct.toFixed(1)}%)
              </Text>
            </Group>
            <Progress value={pct} size="sm" color={color} />
          </div>
        );
      })}
    </Stack>
  );
}

function BreakdownList({
  data,
  total,
  colorMap,
}: {
  data: Record<string, number>;
  total: number;
  colorMap: Record<string, string>;
}) {
  const { t } = useTranslation();
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        {t("dashboard.noDevices")}
      </Text>
    );
  }
  return (
    <Stack gap={6}>
      {entries.map(([status, count]) => {
        const pct = total > 0 ? (count / total) * 100 : 0;
        return (
          <div key={status}>
            <Group justify="space-between" mb={2}>
              <Group gap="xs">
                <Badge
                  size="xs"
                  variant="light"
                  color={colorMap[status] ?? "gray"}
                >
                  {status}
                </Badge>
              </Group>
              <Text size="xs" ff="monospace">
                {count} ({pct.toFixed(1)}%)
              </Text>
            </Group>
            <Progress value={pct} size="sm" color={colorMap[status] ?? "gray"} />
          </div>
        );
      })}
    </Stack>
  );
}

function ActivityChart({ byDay }: { byDay: Record<string, Record<string, number>> }) {
  const days = useMemo(() => {
    const out: { date: string; total: number; actions: Record<string, number> }[] = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const actions = byDay[key] ?? {};
      const total = Object.values(actions).reduce((a, b) => a + b, 0);
      out.push({ date: key, total, actions });
    }
    return out;
  }, [byDay]);

  const max = Math.max(1, ...days.map((d) => d.total));
  return (
    <Stack gap={4}>
      {days.map((d) => {
        const pct = (d.total / max) * 100;
        return (
          <Group key={d.date} gap="xs" wrap="nowrap">
            <Text
              size="xs"
              c="dimmed"
              ff="monospace"
              style={{ width: 70, flexShrink: 0 }}
            >
              {d.date.slice(5)}
            </Text>
            <div style={{ flex: 1 }}>
              <Progress value={pct} size="md" color="blue" />
            </div>
            <Text size="xs" ff="monospace" style={{ width: 32, textAlign: "right" }}>
              {d.total}
            </Text>
          </Group>
        );
      })}
    </Stack>
  );
}

const DEVICE_STATUS_COLORS: Record<string, string> = {
  received: "gray",
  scanned: "cyan",
  keyed: "blue",
  flashed: "violet",
  tested: "indigo",
  labeled: "blue",
  assigned: "blue",
  shipped: "green",
  delivered: "green",
  returned: "yellow",
  defect: "red",
};

const SHIPMENT_STATUS_COLORS: Record<string, string> = {
  draft: "gray",
  ready: "blue",
  shipped: "teal",
  delivered: "green",
};

function actionColor(action: string): string {
  if (action.startsWith("device.flash") || action.startsWith("firmware")) return "violet";
  if (action.startsWith("device.returned") || action.startsWith("device.defect"))
    return "red";
  if (action.startsWith("godex")) return "blue";
  if (action.startsWith("shipment")) return "teal";
  if (action.startsWith("batch")) return "indigo";
  if (action.startsWith("documents")) return "grape";
  if (action.startsWith("device")) return "cyan";
  return "gray";
}

function firstOfMonthStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const PERIOD_SECTIONS = ["scanned", "flashed", "shipped", "returned", "defect"] as const;
type PeriodSection = (typeof PERIOD_SECTIONS)[number];

const PERIOD_SECTION_COLORS: Record<PeriodSection, string> = {
  scanned: "cyan",
  flashed: "violet",
  shipped: "green",
  returned: "yellow",
  defect: "red",
};

function PeriodStatsCard() {
  const { t } = useTranslation();
  const [from, setFrom] = useState<string>(firstOfMonthStr());
  const [to, setTo] = useState<string>(todayStr());
  const [section, setSection] = useState<string>("all");
  const [data, setData] = useState<PeriodStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!from || !to) return;
    if (from > to) {
      setError(t("dashboard.period.invalidRange"));
      setData(null);
      return;
    }
    setError(null);
    let cancelled = false;
    api
      .statsPeriod(from, to)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? JSON.stringify(e.detail) : String(e));
        setData(null);
      });
    return () => {
      cancelled = true;
    };
    // `t` omitted intentionally: stable, and including it would refetch on language change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  const sectionOptions = [
    { value: "all", label: t("dashboard.period.all") },
    ...PERIOD_SECTIONS.map((s) => ({ value: s, label: t(`dashboard.period.${s}`) })),
  ];
  const shown: PeriodSection[] =
    section === "all" ? [...PERIOD_SECTIONS] : [section as PeriodSection];

  return (
    <Card withBorder>
      <Title order={5} mb="sm">
        {t("dashboard.period.title")}
      </Title>
      <Group align="end" gap="sm" wrap="wrap" mb="md">
        <TextInput
          type="date"
          label={t("dashboard.period.from")}
          value={from}
          onChange={(e) => setFrom(e.currentTarget.value)}
          w={160}
        />
        <TextInput
          type="date"
          label={t("dashboard.period.to")}
          value={to}
          onChange={(e) => setTo(e.currentTarget.value)}
          w={160}
        />
        <Select
          label={t("dashboard.period.section")}
          data={sectionOptions}
          value={section}
          onChange={(v) => setSection(v ?? "all")}
          w={180}
        />
      </Group>
      {error && (
        <Text c="dimmed" size="sm">
          {error}
        </Text>
      )}
      {!error && data && (
        section === "all" ? (
          <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="sm">
            {shown.map((s) => (
              <div key={s}>
                <Badge variant="light" color={PERIOD_SECTION_COLORS[s]} mb={4}>
                  {t(`dashboard.period.${s}`)}
                </Badge>
                <Title order={3}>{data.sections[s]}</Title>
              </div>
            ))}
          </SimpleGrid>
        ) : (
          <Group gap="md" align="baseline">
            <Badge size="lg" variant="light" color={PERIOD_SECTION_COLORS[shown[0]]}>
              {t(`dashboard.period.${shown[0]}`)}
            </Badge>
            <Title order={1}>{data.sections[shown[0]]}</Title>
          </Group>
        )
      )}
    </Card>
  );
}

